import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { t } from '@fairstone/ui/core/providers/Intl/locales/translate';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import useAuth from 'context/useAuth';
import useFormFields from 'hooks/useFormFields';

import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import ErrorView from 'components/errors/ErrorView';
import EditProfileWrapper from 'components/forms/profile/EditProfileWrapper';
import TextMaskCustom from 'components/inputs/TextMaskCustom';
import CustomModal from 'components/modals/CustomModal';
import OverlaySpinner from 'components/spinner/OverlaySpinner';
import { PrimaryButtonSto, TextButtonSto } from 'components/sto';
import { AppRoutes } from 'routes';
import { updateProfile } from 'services/AccountService';

const ProfilePhonePage = () => {
    const { formatMessage, locale } = useIntl();
    const history = useHistory();
    const { setUserProfile, userProfile } = useAuth();

    const initialFormValues = {
        mobilePhone: {
            isChanged: false,
            isValid: !!userProfile.mobilePhone,
            value: userProfile.mobilePhone,
        },
        secondaryPhone: {
            isChanged: false,
            isValid: !!userProfile.secondaryPhone,
            value: userProfile.secondaryPhone,
        },
    };
    const { formFields, setFormField } = useFormFields(initialFormValues);
    const { mobilePhone, secondaryPhone } = formFields;
    const [isFormDisabled, setIsFormDisabled] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [enableEdit, setEnableEdit] = useState({
        mobilePhone: false,
        secondaryPhone: false,
    });
    const [showSecondary, setShowSecondary] = useState(!!secondaryPhone.value);
    let redirect = false;

    const initialModal = {
        cancelText: null,
        modalType: null,
        okText: null,
        onCancel: null,
        onOk: null,
        title: null,
        visible: false,
    };
    const [modal, setModal] = useState(initialModal);

    const handleApiError = (error) => {
        const { status } = error?.response || {};
        if (status === 429) {
            setModal(initialModal);
            setIsFormDisabled(true);
            return setApiError('pages.profile.edit.error.limit.exceeded');
        }
        setModal({
            cancelText: null,
            modalType: 'fail',
            okText: 'general.ok',
            onCancel: null,
            onOk: handleGoBack,
            title: 'pages.profile.edit.failed',
            visible: true,
        });
    };

    const handleApiSuccess = (title) => {
        setModal({
            cancelText: null,
            modalType: 'success',
            okText: 'general.ok',
            onCancel: null,
            onOk: handleGoBack,
            title: title,
            visible: true,
        });
    };

    const handleBlur = (event) => {
        const fieldName =
            event.target.name ===
            formatMessage({
                id: `pages.profile.phone.mobile.number`,
            })
                ? 'mobilePhone'
                : 'secondaryPhone';
        const { errorMessage, isValid } = handleValidation(event.target.value, fieldName, true);

        setFormField(fieldName, {
            errorMessage,
            isChanged: userProfile[fieldName].replace(/\D/g, '') !== event.target.value?.replace(/\D/g, ''),
            isValid,
        });

        if (isValid) {
            setEnableEdit((prevState) => ({ ...prevState, [fieldName]: false }));
        }
    };

    const handleChange = (event) => {
        const fieldName =
            event.target.name ===
            formatMessage({
                id: `pages.profile.phone.mobile.number`,
            })
                ? 'mobilePhone'
                : 'secondaryPhone';
        const { errorMessage, isValid } = handleValidation(event.target.value, fieldName);

        setFormField(fieldName, {
            errorMessage,
            isChanged: userProfile[fieldName].replace(/\D/g, '') !== event.target.value?.replace(/\D/g, ''),
            isValid,
            value: event.target.value,
        });
    };

    const handleClickOnDelete = () => {
        if (!userProfile.secondaryPhone) {
            setShowSecondary(false);
            setFormField('secondaryPhone', {
                errorMessage: '',
                isChanged: false,
                isValid: false,
                value: '',
            });
        } else {
            setModal({
                cancelText: 'general.cancel',
                modalType: 'confirmation',
                okText: 'general.delete',
                onCancel: handleGoBack,
                onOk: handleDelete,
                title: 'pages.profile.edit.phone.confirmation',
                visible: true,
            });
        }
    };

    const handleDelete = async () => {
        try {
            setIsSubmitting(true);
            const res = await updateProfile({ secondaryPhone: '' });
            setUserProfile((prevState) => ({
                ...prevState,
                secondaryPhone: res.find((o) => o.fieldToUpdate === 'secondaryPhone').fieldData,
            }));
            setFormField('secondaryPhone', {
                isChanged: true,
                isValid: false,
                value: null,
            });
            setShowSecondary(false);
            handleApiSuccess('pages.profile.edit.phone.deleted');
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (name) => {
        setEnableEdit((prevState) => ({
            ...prevState,
            [name]: true,
        }));
    };

    const handleGoBack = () => {
        setModal(initialModal);
        setEnableEdit({ mobilePhone: false, secondaryPhone: false });
        if (redirect) {
            history.push(`/${locale}/${formatMessage({ id: 'tab.label.profile' }).toLowerCase()}`);
        }
    };

    const handleSave = async (e) => {
        try {
            let apiSuccessMessage;
            e.preventDefault();
            setIsSubmitting(true);
            const updatedData = {};
            if (secondaryPhone.isChanged) {
                updatedData.secondaryPhone = secondaryPhone.value.replace(/\D/g, '');
                apiSuccessMessage = 'pages.profile.edit.phone.updated.secondary';
            }
            if (mobilePhone.isChanged) {
                updatedData.mobilePhone = mobilePhone.value.replace(/\D/g, '');
                apiSuccessMessage = 'pages.profile.edit.phone.updated.primary';
            }
            const res = await updateProfile(updatedData);
            setUserProfile((prevState) => ({
                ...prevState,
                mobilePhone: res.find((o) => o.fieldToUpdate === 'mobilePhone')?.fieldData || prevState.mobilePhone,
                secondaryPhone:
                    res.find((o) => o.fieldToUpdate === 'secondaryPhone')?.fieldData || prevState.secondaryPhone,
            }));
            redirect = true;
            if (secondaryPhone.isChanged && mobilePhone.isChanged) {
                apiSuccessMessage = 'pages.profile.edit.phone.updated';
            }
            handleApiSuccess(apiSuccessMessage);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleValidation = (value, fieldName, onBlur) => {
        let newValue = value.replace(/\s/g, '');
        newValue = newValue.replace(/\D/g, '');
        const isValidPhoneLength = newValue.length === 10;
        let isValid;
        const isUniqueNumber = value !== (fieldName === 'mobilePhone' ? secondaryPhone.value : mobilePhone.value);

        if (onBlur) {
            isValid = isUniqueNumber && isValidPhoneLength;
        } else {
            isValid =
                newValue.length < 10 && !formFields[fieldName].isValid ? null : isUniqueNumber && isValidPhoneLength;
        }

        if (isValid) return { isValid };

        let errorMessage;
        if (!isUniqueNumber) {
            errorMessage =
                fieldName === 'mobilePhone'
                    ? 'form.registration.phoneNumber.field.primary.error'
                    : 'form.registration.phoneNumber.field.secondary.error';
        } else {
            errorMessage = 'form.registration.phoneNumber.field.error';
        }

        return {
            errorMessage,
            isValid,
        };
    };

    const isFormValid = useMemo(() => {
        if (showSecondary) {
            return secondaryPhone.isValid && mobilePhone.isValid && (mobilePhone.isChanged || secondaryPhone.isChanged);
        }
        return mobilePhone.isValid && mobilePhone.isChanged;
    }, [secondaryPhone.value, mobilePhone.value, showSecondary]);

    const mobileRef = useRef(null);
    const secondaryRef = useRef(null);

    useEffect(() => {
        mobileRef.current.focus();
    }, [enableEdit.mobilePhone]);

    useEffect(() => {
        secondaryRef.current?.focus();
    }, [enableEdit.secondaryPhone]);

    if (isSubmitting)
        return (
            <OverlaySpinner>
                <p>{t('spinner.submitting.information')}</p>
                <p>{t('spinner.message.2')}</p>
            </OverlaySpinner>
        );

    return (
        <EditProfileWrapper goBack={AppRoutes.profile} title="pages.profile.title.phone">
            <Box className="profile-phone-container">
                <form className="profile-phone-content" onSubmit={handleSave}>
                    <fieldset disabled={isFormDisabled}>
                        {apiError && <ErrorView error={apiError} />}
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                className: 'text-input',
                                endAdornment: !enableEdit['mobilePhone'] && (
                                    <IconButton
                                        className="edit-icon"
                                        disabled={isFormDisabled}
                                        onClick={() => handleEdit('mobilePhone')}
                                        title={formatMessage({ id: 'pages.profile.edit' })}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                ),
                                inputComponent: TextMaskCustom,
                            }}
                            autoComplete="off"
                            className="text-field"
                            error={!mobilePhone.isValid}
                            fullWidth
                            helperText={
                                !mobilePhone.isValid &&
                                mobilePhone.errorMessage && (
                                    <span className="error-text">
                                        <FormattedMessage id={mobilePhone.errorMessage} />
                                    </span>
                                )
                            }
                            inputProps={{
                                'data-testid': 'mobile',
                                definitions: {
                                    '#': /[2-9]/,
                                },
                                disabled: !enableEdit['mobilePhone'],
                                mask: '(#00) 000-0000',
                            }}
                            inputRef={mobileRef}
                            label={
                                <Box className="text-label-container">
                                    <Typography className="text-label">
                                        <FormattedMessage id="pages.profile.phone.mobile.number" />
                                    </Typography>
                                    <Typography className="text-label-brackets">
                                        <FormattedMessage id="pages.profile.phone.primary" />
                                    </Typography>
                                    <Typography className="text-label">*</Typography>
                                </Box>
                            }
                            name={formatMessage({ id: `pages.profile.phone.mobile.number` })}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={mobilePhone.value}
                            variant="standard"
                        />
                        {showSecondary && (
                            <TextField
                                InputLabelProps={{ className: 'text-label', shrink: true }}
                                InputProps={{
                                    className: 'text-input',
                                    endAdornment: !enableEdit['secondaryPhone'] && (
                                        <IconButton
                                            className="edit-icon"
                                            disabled={isFormDisabled}
                                            onClick={() => handleEdit('secondaryPhone')}
                                            title={formatMessage({ id: 'pages.profile.edit' })}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    ),
                                    inputComponent: TextMaskCustom,
                                }}
                                autoComplete="off"
                                className="text-field"
                                error={!secondaryPhone.isValid}
                                fullWidth
                                helperText={
                                    !secondaryPhone.isValid &&
                                    secondaryPhone.errorMessage && (
                                        <span className="error-text">
                                            <FormattedMessage id={secondaryPhone.errorMessage} />
                                        </span>
                                    )
                                }
                                inputProps={{
                                    'data-testid': 'secondary',
                                    definitions: {
                                        '#': /[2-9]/,
                                    },
                                    disabled: !enableEdit['secondaryPhone'],
                                    mask: '(#00) 000-0000',
                                }}
                                inputRef={secondaryRef}
                                label={
                                    <Box className="text-label-container">
                                        <Typography className="text-label">
                                            <FormattedMessage id="pages.profile.phone.secondary.number" />
                                        </Typography>
                                        <Typography className="text-label-brackets">
                                            <FormattedMessage id="pages.profile.phone.optional" />
                                        </Typography>
                                    </Box>
                                }
                                name={formatMessage({
                                    id: 'pages.profile.phone.secondary.number',
                                })}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={secondaryPhone.value}
                                variant="standard"
                            />
                        )}
                        <Box className="buttons-container">
                            <Box className={`secondary-buttons ${showSecondary ? 'flex-end' : 'flex-start'}`}>
                                {!showSecondary && (
                                    <TextButtonSto
                                        className="add-phone"
                                        disabled={showSecondary}
                                        labelKey="pages.profile.edit.add.number"
                                        onClick={() => {
                                            setShowSecondary(true);
                                            setEnableEdit((prevState) => ({
                                                ...prevState,
                                                secondaryPhone: true,
                                            }));
                                        }}
                                        size="small"
                                    />
                                )}
                                {showSecondary && (
                                    <TextButtonSto
                                        className="delete-phone"
                                        labelKey="pages.profile.edit.delete.number"
                                        onClick={handleClickOnDelete}
                                        size="small"
                                    />
                                )}
                            </Box>

                            <PrimaryButtonSto disabled={!isFormValid} labelKey="general.save" type="submit" />
                        </Box>
                    </fieldset>
                </form>
                <CustomModal
                    cancelText={modal.cancelText}
                    modalType={modal.modalType}
                    okText={modal.okText}
                    onCancel={modal.onCancel}
                    onOk={modal.onOk}
                    visible={modal.visible}
                >
                    <p>
                        <FormattedMessage id={modal.title} />
                    </p>
                </CustomModal>
            </Box>
        </EditProfileWrapper>
    );
};

export default ProfilePhonePage;
